// LUMINA · utils/baziCalc.js
// Pure Bazi calculation functions — no React, no side effects.

import { TG, DZ, TG_EN, DZ_EN, SE, BE, EEN, JDAY,
         LICHUN_EX, JIAZI_60, SHISHEN_MAP, SS_EN, SS_MEANING,
         BRANCH_MAIN, CHONG, SHENG, KE_MAP, C } from '../constants/baziData';

export function getLichunDay(y){return LICHUN_EX[y]||4;}
export function gzYear(y,m,d){const ld=getLichunDay(y);return(m<2||(m===2&&d<ld))?y-1:y;}
export function jdn(y,m,d){const a=Math.floor((14-m)/12),yr=y+4800-a,mo=m+12*a-3;return d+Math.floor((153*mo+2)/5)+365*yr+Math.floor(yr/4)-Math.floor(yr/100)+Math.floor(yr/400)-32045;}
export function yearP(y,m,d){const gy=gzYear(y,m,d),o=((gy-4)%60+60)%60;return{si:o%10,bi:o%12};}
export function monthP(y,m,d){const gy=gzYear(y,m,d);let mo=m,my=gy,jd=JDAY[mo-1];if(d<jd){mo--;if(!mo){mo=12;my--;}}const bi=mo%12,ysi=((my-4)%10+10)%10,ms=[2,4,6,8,0,2,4,6,8,0][ysi],off=mo>=2?mo-2:11;return{si:(ms+off)%10,bi};}
export function dayP(y,m,d){const j=jdn(y,m,d),EPOCH=2443691,delta=((j-EPOCH)%60+60)%60;return{si:delta%10,bi:delta%12};}
export function hourP(h,dsi){const hb=[[23,1,0],[1,3,1],[3,5,2],[5,7,3],[7,9,4],[9,11,5],[11,13,6],[13,15,7],[15,17,8],[17,19,9],[19,21,10],[21,23,11]];let bi=0;for(const[s,e,i]of hb){if(s>e?(h>=s||h<e):(h>=s&&h<e)){bi=i;break;}}return{si:([0,2,4,6,8][dsi%5]+bi)%10,bi};}
export function pillar(p){return{gz:TG[p.si]+DZ[p.bi],en:`${TG_EN[p.si]}-${DZ_EN[p.bi]}`,se:SE[TG[p.si]],be:BE[DZ[p.bi]]};}
export function calcBazi(y,m,d,h){return{year:pillar(yearP(y,m,d)),month:pillar(monthP(y,m,d)),day:pillar(dayP(y,m,d)),hour:pillar(hourP(h,dayP(y,m,d).si))};}


// ── Score & analysis ─────────────────────────────────────────────────────────
export function getShishen(ds, s){return SHISHEN_MAP[ds]?.[s]||"—";}

export function calcScore(pillars){
  const el={木:0,火:0,土:0,金:0,水:0};
  for(const p of Object.values(pillars)){el[p.se]++;el[p.be]++;}
  const pct={};for(const[k,v]of Object.entries(el))pct[EEN[k]]=+(v/8*100).toFixed(1);
  const bal=Math.max(0,Math.min(100,100-Math.round(Object.values(pct).reduce((s,v)=>s+Math.abs(v-20),0)/5*2.8)));
  const vit=Math.max(0,Math.min(100,Math.round(((pct.Wood||0)+(pct.Fire||0))/40*100*0.7+bal*0.3)));
  let ke=0;for(const[a,t]of Object.entries(KE_MAP))if((pct[a]||0)>28&&(pct[t]||0)<12)ke+=15;
  const stress=Math.max(0,Math.min(100,Math.round(ke+(100-bal)*0.5)));
  const mom=Math.max(0,Math.min(100,Math.round(((pct.Metal||0)+(pct.Water||0))/40*100*0.6+bal*0.4)));
  const rel=Math.max(0,Math.min(100,Math.round((pct.Earth||0)/20*70+bal*0.3)));
  const overall=Math.max(0,Math.min(100,Math.round(bal*0.4+vit*0.2+(100-stress)*0.2+mom*0.1+rel*0.1)));
  const tier=overall>=78?{label:"High Coherence",color:C.sage}:overall>=55?{label:"Moderate Flux",color:C.gold}:{label:"Active Recalibration",color:C.stress};
  const dom=Object.entries(pct).sort((a,b)=>b[1]-a[1])[0][0];
  const lack=Object.entries(pct).sort((a,b)=>a[1]-b[1])[0][0];
  const PROTO={
    Fire:{p:{en:"Vajrakilaya stabilization (21-day cycle)",zh:"普巴金刚修持（21天周期）"},d:{en:"Metal-enhancing diet · white foods, pungent",zh:"白色食物为主：梨、白萝卜、银耳"},g:{en:"excess Fire disrupting Metal equilibrium",zh:"火元素过旺扰乱金元素平衡"},deity:"普巴金刚 Vajrakilaya"},
    Wood:{p:{en:"Chenrezig compassion practice (14-day cycle)",zh:"千手观音修持（14天周期）"},d:{en:"Earth-grounding diet · root vegetables, sweet",zh:"黄色根茎食物：南瓜、红薯、蜂蜜"},g:{en:"excess Wood creating Earth imbalance",zh:"木元素过旺影响土元素平衡"},deity:"千手观音 Chenrezig"},
    Water:{p:{en:"Tummo inner fire activation (7-day intensive)",zh:"那若六法拙火修持（7天集中）"},d:{en:"Fire-activating diet · warming foods, bitter",zh:"温热食物：姜、辣椒、苦瓜"},g:{en:"excess Water suppressing Fire vitality",zh:"水元素过旺抑制火元素生命力"},deity:"那若六法 Naropa Six Yogas"},
    Metal:{p:{en:"Manjushri wisdom practice (21-day cycle)",zh:"文殊菩萨修持（21天周期）"},d:{en:"Wood-nourishing diet · green foods, sour",zh:"绿色食物：菠菜、西兰花、酸味水果"},g:{en:"excess Metal restricting Wood creativity",zh:"金元素过旺限制木元素创造力"},deity:"文殊菩萨 Manjushri"},
    Earth:{p:{en:"Green Tara swift-action practice (14-day cycle)",zh:"绿度母修持（14天周期）"},d:{en:"Water-replenishing diet · dark foods, salty",zh:"黑色食物：黑芝麻、黑豆、海带"},g:{en:"excess Earth dampening Water clarity",zh:"土元素过旺阻碍水元素清明"},deity:"绿度母 Green Tara"}};
  const proto=PROTO[dom]||PROTO.Earth;
  return{overall,vitality:vit,stress,momentum:mom,relational:rel,tier,pct,dominant:dom,lacking:pct[lack]<8?lack:null,proto};
}

export function calcShishen(pillars,lang="en"){
  const ds=pillars.day.gz[0];
  return["year","month","day","hour"].map(k=>{
    const p=pillars[k],stem=p.gz[0],branch=p.gz[1];
    const ss=getShishen(ds,stem),bstem=BRANCH_MAIN[branch]||"",bss=bstem?getShishen(ds,bstem):"—";
    const m=SS_MEANING[ss]||"";return{pillar:k,gz:p.gz,stem,branch,ss,bss,ss_en:SS_EN[ss]||"",meaning:typeof m==="object"?m[lang]||m.en:m};
  });
}

export function calcDayun(pillars,birthYear,gender="M",lang="en"){
  const ysi=TG.indexOf(pillars.year.gz[0]);
  const forward=(ysi%2===0&&gender==="M")||(ysi%2!==0&&gender==="F");
  const mIdx=JIAZI_60.indexOf(pillars.month.gz)||0;
  return Array.from({length:10},(_,i)=>{
    const idx=((forward?mIdx+i+1:mIdx-i-1)%60+60)%60;
    const gz=JIAZI_60[idx],stem=gz[0],branch=gz[1];
    const de=SE[stem]||"",be2=BE[branch]||"";
    const dayElem=SE[pillars.day.gz[0]]||"";
    let q="平",descEn="Stable period, steady progress",descZh="平稳期，稳步前进";
    if(SHENG[de]===dayElem||SHENG[be2]===dayElem){q="吉";descEn="Favorable — energy supports growth";descZh="吉运 — 能量支持成长";}
    else if(KE_MAP[de]===dayElem||KE_MAP[be2]===dayElem){q="凶";descEn="Challenging — consolidate and be cautious";descZh="注意期 — 宜稳健保守";}
    const desc=lang==="zh"?descZh:descEn;
    return{order:i+1,gz,stem,branch,startAge:3+i*10,endAge:12+i*10,quality:q,desc};
  });
}

export function analyzeLiunian(pillars,lang="en"){
  const gz="丙午",stem=gz[0],branch=gz[1];
  const ds=pillars.day.gz[0],ss=getShishen(ds,stem);
  let en=`2026 (${gz}) — ${ss} energy activated. `;
  let zh=`2026年（${gz}）— ${ss}能量启动。`;
  if(["正官","七杀"].includes(ss)){en+="Career opportunities and pressure. Watch health.";zh+="事业机遇与压力并存，注意健康。";}
  else if(["正财","偏财"].includes(ss)){en+="Wealth potential activated. Pursue business opportunities.";zh+="财运潜力激活，把握商业机遇。";}
  else if(["食神","伤官"].includes(ss)){en+="Creative expression highlighted. Showcase talents.";zh+="才华展现之年，适合创作与技术发挥。";}
  else if(["正印","偏印"].includes(ss)){en+="Learning and benefactor support. Excellent for study.";zh+="学习进修、贵人相助之年。";}
  else{en+="Focus on partnerships and collaborations.";zh+="专注合作与人际关系。";}
  if(CHONG[branch]===pillars.day.gz[1]){en+=" ⚠️ Year clashes Day pillar — major transitions ahead.";zh+=" ⚠️ 流年冲日柱 — 重大变化之年，需谨慎应对。";}
  return lang==="zh"?zh:en;
}
